<script setup lang="ts">
/* eslint-disable no-undef */
/**
 * QxInsight Product Route Page
 *
 * Static route for QxInsight product landing page.
 * Queries markdown content from /content/products/insight.md
 * and renders using ProductPageTemplate component.
 *
 * Path: /insight
 * Content Source: content/products/insight.md
 * Integration: Nuxt Content + ProductPageTemplate + BookingsCTA
 */

import BookingsCTA from '../../components/cta/BookingsCTA.vue'
import FormFallbackCTA from '../../components/cta/FormFallbackCTA.vue'
import {
  buildBreadcrumbJsonLd,
  buildProductJsonLd,
  createJsonLdScript,
  toAbsoluteUrl,
} from '../../composables/useStructuredData'

/**
 * Access app configuration for bookings URL
 */
const appConfig = useAppConfig()

/**
 * Bookings API availability flag
 * In production, this would be connected to health check endpoint
 * For MVP: Toggle manually via browser console: window.__bookingsAvailable = false
 */
const bookingsAvailable = ref(true)

// Allow runtime override for testing (SSR-safe)
if (typeof window !== 'undefined') {
  (window as any).__bookingsAvailable = bookingsAvailable
}

/**
 * Product content interface matching markdown frontmatter schema
 */
interface ProductContent {
  title: string;
  description: string;
  image?: string;
  keywords?: string[];
  publishedAt?: string;
  body?: {
    children: Array<any>;
  };
}

/**
 * Query markdown content for QxInsight product.
 * Uses useAsyncData for SSG compatibility and caching.
 * Handles prerendering context where queryContent may not be available.
 */
const { data: insightData } = await useAsyncData('insight-product', async () => {
  try {
    // @ts-expect-error - Nuxt Content auto-import
    const queryContentFn = typeof queryContent !== 'undefined' ? queryContent : null
    if (queryContentFn) {
      return await queryContentFn<ProductContent>('products/insight').findOne()
    }
    return null
  } catch (err) {
    // Handle prerendering context where queryContent is not available
    console.warn('Failed to load content during prerendering:', err)
    return null
  }
});

const CANONICAL_URL = 'https://qx.qiksolve.com/insight';
const SOCIAL_IMAGE_FALLBACK = 'https://qx.qiksolve.com/images/products/insight-hero.svg';
const SITE_ROOT = 'https://qx.qiksolve.com';
const { emitSchemaValidationPassed, emitSchemaValidationFailed } = useAnalytics();

const productImageUrl = computed(() =>
  toAbsoluteUrl(SITE_ROOT, insightData.value?.image) ?? SOCIAL_IMAGE_FALLBACK
);

const productSchema = computed(() =>
  buildProductJsonLd({
    name: insightData.value?.title || 'QxInsight',
    description:
      insightData.value?.description ||
      'Drive data-driven quality decisions with real-time analytics and dashboards.',
    url: CANONICAL_URL,
    image: productImageUrl.value,
    brandName: 'QxAIOS',
  })
);

const breadcrumbSchema = computed(() =>
  buildBreadcrumbJsonLd([
    { name: 'Home', item: `${SITE_ROOT}/` },
    { name: 'Product', item: `${SITE_ROOT}/product` },
    { name: 'Insight', item: CANONICAL_URL },
  ])
);

if (productSchema.value.schema) {
  emitSchemaValidationPassed('product', 'product');
} else {
  emitSchemaValidationFailed('product', 'product', productSchema.value.reasonCodes);
}

if (breadcrumbSchema.value) {
  emitSchemaValidationPassed('product', 'breadcrumb');
} else {
  emitSchemaValidationFailed('product', 'breadcrumb', ['missing_breadcrumb_items']);
}

/**
 * Extract markdown sections from body AST.
 * Each section is identified by its heading text.
 */
const extractSection = (content: ProductContent | null, sectionHeading: string): string => {
  if (!content?.body?.children) return '';

  const headingIndex = content.body.children.findIndex(
    (node: any) =>
      node.tag === 'h2' &&
      node.children?.[0]?.value?.toLowerCase() === sectionHeading.toLowerCase()
  );

  if (headingIndex === -1) return '';

  // Collect nodes between this heading and next heading
  const sectionNodes = [];
  for (let i = headingIndex + 1; i < content.body.children.length; i++) {
    const node = content.body.children[i];
    if (node.tag === 'h2') break; // Stop at next section
    sectionNodes.push(node);
  }

  // Convert nodes to HTML string
  return sectionNodes
    .map((node: any) => {
      if (node.tag === 'p') {
        return `<p>${node.children?.map((c: any) => c.value || '').join('')}</p>`;
      } else if (node.tag === 'ul') {
        const items = node.children
          ?.map((li: any) => `<li>${li.children?.map((c: any) => c.value || '').join('')}</li>`)
          .join('');
        return `<ul class="list-disc list-inside space-y-2 text-neutral-700">${items}</ul>`;
      }
      return '';
    })
    .join('');
};

/**
 * SEO metadata for insight product page
 * Includes Open Graph (OG), Twitter Card, and canonical URL for social sharing and SEO
 */
useSeoMeta({
  // Basic page metadata
  title: insightData.value?.title || 'QxInsight - Quality Analytics & Executive Dashboards',
  description:
    insightData.value?.description ||
    'Drive data-driven quality decisions with real-time analytics and dashboards.',
  keywords: insightData.value?.keywords?.join(', '),

  // Open Graph metadata (for Facebook, LinkedIn, etc.)
  ogTitle: insightData.value?.title || 'QxInsight',
  ogDescription:
    insightData.value?.description ||
    'Drive data-driven quality decisions with real-time analytics and dashboards.',
  ogImage: insightData.value?.image
    ? `https://qx.qiksolve.com${insightData.value.image}`
    : SOCIAL_IMAGE_FALLBACK,
  ogUrl: CANONICAL_URL,
  ogType: 'website',

  // Twitter Card metadata (for Twitter sharing)
  twitterCard: 'summary_large_image',
  twitterTitle: insightData.value?.title || 'QxInsight',
  twitterDescription:
    insightData.value?.description ||
    'Drive data-driven quality decisions with real-time analytics and dashboards.',
  twitterImage: insightData.value?.image
    ? `https://qx.qiksolve.com${insightData.value.image}`
    : SOCIAL_IMAGE_FALLBACK,

  // Canonical URL for SEO
  canonical: CANONICAL_URL,
});

useHead({
  link: [
    {
      rel: 'canonical',
      href: CANONICAL_URL,
    },
  ],
  script: [
    ...(productSchema.value.schema ? [createJsonLdScript(productSchema.value.schema)] : []),
    ...(breadcrumbSchema.value ? [createJsonLdScript(breadcrumbSchema.value)] : []),
  ],
});
</script>

<template>
  <ProductPageTemplate class="theme-insight">
    <!-- Hero Section -->
    <template #hero>
      <div class="text-center space-y-6">
        <h1 class="text-4xl sm:text-5xl md:text-6xl font-bold text-product tracking-tight">
          {{ insightData?.title || 'QxInsight' }}
        </h1>
        <p class="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto">
          {{ insightData?.description || 'Drive data-driven quality decisions with real-time analytics.' }}
        </p>
        <div
          v-if="insightData?.image"
          class="mt-8"
        >
          <NuxtImg
            :src="insightData.image"
            :alt="`${insightData.title} hero illustration`"
            fetchpriority="high"
            format="webp"
            sizes="xs:100vw sm:448px"
            class="mx-auto max-w-md w-full h-auto"
          />
        </div>
        <!-- Hero CTA -->
        <div class="mt-8">
          <BookingsCTA
            v-if="bookingsAvailable"
            button-text="Book a Demo"
            :bookings-url="appConfig?.bookings?.url || '/contact/'"
            variant="primary"
            location="hero"
            product="insight"
            data-product="insight"
          />
          <FormFallbackCTA
            v-else
            button-text="Contact Sales"
            form-url="/contact"
            fallback-reason="api_unavailable"
            location="hero"
            product="insight"
          />
        </div>
      </div>
    </template>

    <!-- Problem Section -->
    <template #problem>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          The Problem
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(insightData, 'problem')" />
      </div>
    </template>

    <!-- Solution Section -->
    <template #solution>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          The Solution
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(insightData, 'solution')" />
      </div>
    </template>

    <!-- How It Works / Benefits Section -->
    <template #howItWorks>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          Benefits
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(insightData, 'benefits')" />
        <!-- Mid-page CTA -->
        <div class="mt-8 text-center">
          <BookingsCTA
            v-if="bookingsAvailable"
            button-text="Schedule a Call"
            :bookings-url="appConfig?.bookings?.url || '/contact/'"
            variant="secondary"
            location="mid"
            product="insight"
            data-product="insight"
          />
          <FormFallbackCTA
            v-else
            button-text="Contact Sales"
            form-url="/contact"
            fallback-reason="api_unavailable"
            location="mid"
            product="insight"
          />
        </div>
      </div>
    </template>

    <!-- Use Cases Section -->
    <template #useCases>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          Use Cases
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(insightData, 'use cases')" />
      </div>
    </template>

    <!-- Footer CTA -->
    <template #cta>
      <div class="text-center">
        <BookingsCTA
          v-if="bookingsAvailable"
          button-text="Get Started"
          :bookings-url="appConfig?.bookings?.url || '/contact/'"
          variant="secondary"
          location="footer"
          product="insight"
          data-product="insight"
        />
        <FormFallbackCTA
          v-else
          button-text="Get Started"
          form-url="/contact"
          fallback-reason="api_unavailable"
          location="footer"
          product="insight"
        />
      </div>
    </template>
  </ProductPageTemplate>
</template>
